<template>
  <div class="report-datepicker">
    <VueDatePicker
      v-model="innerValue"
      :enableTimePicker="false"
      :format="formatDate"
      :previewFormat="formatDate"
      :minDate="minDateObj"
      :maxDate="maxDateObj"
      :disabledDates="resolveDisabledDates"
      :clearable="false"
      :hideInputIcon="hideInputIcon"
      :dayNames="dayNames"
      :weekStart="weekStart"
      :locale="locale"
      monthNameFormat="long"
      autoApply
      :closeOnAutoApply="true"
      :placeholder="placeholder"
      menuClassName="report-dp-menu"
      inputClassName="report-dp-input"
      calendarCellClassName="report-dp-cell"
    />
  </div>
</template>

<script>
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';

function parseIso(iso) {
  if (!iso) return null;
  const s = String(iso).slice(0, 10);
  const [ y, m, d ] = s.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function toIso(date) {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function formatMmDdYyyy(date) {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
}

function snapToAllowedWeekday(date, allowedWeekdays) {
  if (!date || !allowedWeekdays?.length) return date;
  const d = date instanceof Date ? new Date(date.getTime()) : new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  const allowed = new Set(allowedWeekdays.map(Number));
  if (allowed.has(d.getDay())) return d;
  // Walk back up to 6 days to nearest allowed weekday.
  for (let i = 0; i < 7; i += 1) {
    d.setDate(d.getDate() - 1);
    if (allowed.has(d.getDay())) return d;
  }
  return date;
}

export default {
  name: 'ReportConstrainedDatePicker',
  components: { VueDatePicker },
  props: {
    modelValue: {
      type: String,
      default: '',
    },
    minDate: {
      type: String,
      default: '',
    },
    maxDate: {
      type: String,
      default: '',
    },
    /** 0=Sun … 6=Sat. When set, other weekdays are disabled and value snaps to nearest allowed. */
    allowedWeekdays: {
      type: Array,
      default: null,
    },
    disabledDates: {
      type: Function,
      default: null,
    },
    hideInputIcon: {
      type: Boolean,
      default: true,
    },
    placeholder: {
      type: String,
      default: 'MM/DD/YYYY',
    },
    locale: {
      type: String,
      default: 'en-US',
    },
    weekStart: {
      type: Number,
      default: 0,
    },
    dayNames: {
      type: Array,
      default() {
        return [ 'Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa' ];
      },
    },
  },
  emits: ['update:modelValue'],
  computed: {
    innerValue: {
      get() {
        return parseIso(this.modelValue);
      },
      set(val) {
        let next = val;
        if (this.allowedWeekdays?.length) {
          next = snapToAllowedWeekday(val, this.allowedWeekdays);
        }
        if (!next) return;
        this.$emit('update:modelValue', toIso(next));
      },
    },
    minDateObj() {
      return parseIso(this.minDate);
    },
    maxDateObj() {
      return parseIso(this.maxDate);
    },
  },
  methods: {
    formatDate(date) {
      return formatMmDdYyyy(date);
    },
    resolveDisabledDates(date) {
      if (typeof this.disabledDates === 'function') {
        return this.disabledDates(date);
      }
      if (this.allowedWeekdays?.length) {
        return !this.allowedWeekdays.map(Number).includes(date.getDay());
      }
      return false;
    },
  },
};
</script>

<style>
.report-datepicker {
  width: 100%;
}

.report-datepicker .dp__main {
  font-family: Roobert-Regular, 'Segoe UI', Arial, sans-serif;
}

.report-datepicker .dp__input_wrap {
  width: 100%;
}

.report-datepicker .report-dp-input,
.report-datepicker .dp__input {
  height: 38px;
  border: none !important;
  border-radius: 0.5rem !important;
  background: #e0f2fe !important;
  color: #31333f !important;
  font-size: 15px !important;
  font-family: Roobert-Regular, 'Segoe UI', Arial, sans-serif !important;
  padding: 0 0.75rem !important;
  box-shadow: none !important;
}

.report-datepicker .dp__input:focus,
.report-datepicker .dp__input_focus {
  border: none !important;
  box-shadow: 0 0 0 2px rgba(0, 102, 204, 0.35) !important;
}

.report-datepicker .dp__input_icon {
  display: none !important;
}

.report-dp-menu.dp__menu,
.dp__menu.report-dp-menu {
  --dp-background-color: #ffffff;
  --dp-text-color: #31333f;
  --dp-hover-color: #e0f2fe;
  --dp-hover-text-color: #31333f;
  --dp-hover-icon-color: #31333f;
  --dp-primary-color: #0066cc;
  --dp-primary-text-color: #ffffff;
  --dp-secondary-color: #a3a8b8;
  --dp-border-color: #e6eaf1;
  --dp-menu-border-color: #d6d9e0;
  --dp-border-color-hover: #c4c9d4;
  --dp-disabled-color: #f0f2f6;
  --dp-disabled-color-text: #a3a8b8;
  --dp-icon-color: #545c66;
  --dp-cell-size: 36px;
  --dp-cell-padding: 2px;
  --dp-cell-border-radius: 50%;
  --dp-border-radius: 0.5rem;
  --dp-month-year-row-height: 36px;
  --dp-font-family: Roobert-Regular, 'Segoe UI', Arial, sans-serif;
  --dp-font-size: 15px;
  border-radius: 0.65rem;
  border: 1px solid #d6d9e0;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.14);
  padding: 10px 12px 12px;
  font-family: Roobert-Regular, 'Segoe UI', Arial, sans-serif;
}

.report-dp-menu .dp__calendar_header_item {
  font-size: 12px;
  font-weight: 600;
  color: #7c8194;
  text-transform: none;
}

.report-dp-menu .dp__month_year_select {
  font-weight: 600;
  font-size: 14px;
}

.report-dp-menu .report-dp-cell,
.report-dp-menu .dp__cell_inner {
  border-radius: 50% !important;
  font-size: 13px;
}

.report-dp-menu .dp__active_date {
  background: #0066cc !important;
  color: #fff !important;
  border-radius: 50% !important;
}

.report-dp-menu .dp__today {
  border: 1px solid #0066cc !important;
  background: transparent !important;
  color: #31333f !important;
}

.report-dp-menu .dp__today.dp__active_date {
  background: #0066cc !important;
  color: #fff !important;
}

.report-dp-menu .dp__date_hover:hover,
.report-dp-menu .dp__cell_inner:hover {
  background: #e0f2fe !important;
  color: #31333f !important;
  border-radius: 50% !important;
}

.report-dp-menu .dp__cell_offset {
  color: #c0c4cc;
}
</style>
